import { usePageTitle } from "@/lib/usePageTitle";
import { ArrowRight, CalendarDays, Check, Mail, Briefcase, MapPin } from "lucide-react";
import FadeIn from "@/components/site/FadeIn";
import { PrimaryLink, CoralLink, PageHero } from "@/components/site/ui";
import { COMPANY } from "@/data/content";
import { useLocale } from "@/i18n/LocaleContext";

const formatDate = (iso, tag) =>
  new Date(iso).toLocaleDateString(tag, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

export default function Aktuelles() {
  usePageTitle("news");
  const { t, path, tag } = useLocale();
  const applyHref = `mailto:${COMPANY.email}?subject=${encodeURIComponent(t.newsPage.applySubject)}`;

  return (
    <div data-testid="news-page">
      <PageHero
        eyebrow={t.newsPage.eyebrow}
        title={t.newsPage.title}
        subtitle={t.newsPage.subtitle}
      />

      {/* News */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-600 text-brand-dark">
              {t.newsPage.newsTitle}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.newsPage.newsLead}</p>
          </FadeIn>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {t.news.map((n, i) => (
              <FadeIn key={n.id} delay={(i % 3) * 0.1}>
                <article
                  data-testid={`news-card-${n.id}`}
                  className="flex flex-col h-full rounded-2xl bg-cream border border-border p-7 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_12px_40px_rgb(44,51,51,0.10)]"
                >
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 font-600 text-brand">
                      {n.category}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="w-4 h-4" /> {formatDate(n.date, tag)}
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-600 text-brand-dark leading-snug">
                    {n.title}
                  </h3>
                  <p className="mt-3 text-base text-muted-foreground leading-relaxed flex-1">
                    {n.excerpt}
                  </p>
                  <p className="mt-3 text-base text-ink leading-relaxed">
                    {n.body}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Stellenanzeigen */}
      <section id="stellenanzeigen" className="bg-cream scroll-mt-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-coral/20 px-4 py-1.5 text-sm font-600 uppercase tracking-wide text-coral-hover">
              <Briefcase className="w-4 h-4" /> {t.newsPage.careerEyebrow}
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-600 text-brand-dark">{t.newsPage.careerTitle}</h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.newsPage.careerLead}</p>
          </FadeIn>

          <div className="mt-12 space-y-8">
            {t.jobs.map((job) => (
              <FadeIn key={job.id}>
                <article
                  data-testid={`job-${job.id}`}
                  className="rounded-2xl bg-white border border-border p-8 md:p-10 shadow-[0_8px_30px_rgb(44,51,51,0.06)]"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-600 text-brand-dark">
                        {job.title}
                      </h3>
                      <div className="mt-3 flex flex-wrap gap-3 text-base text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-brand font-600">
                          {job.type}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-4 h-4" /> {job.location}
                        </span>
                      </div>
                    </div>
                    <CoralLink
                      href={applyHref}
                      data-testid={`job-apply-${job.id}`}
                      className="shrink-0"
                    >
                      <Mail className="w-5 h-5" /> {t.newsPage.applyNow}
                    </CoralLink>
                  </div>

                  <p className="mt-6 text-lg text-ink leading-relaxed">
                    {job.intro}
                  </p>

                  <div className="mt-8 grid md:grid-cols-3 gap-8">
                    <JobList title={t.newsPage.tasks} items={job.tasks} />
                    <JobList title={t.newsPage.profile} items={job.requirements} />
                    <JobList title={t.newsPage.offer} items={job.offer} />
                  </div>

                  <div className="mt-8 rounded-xl bg-cream border border-border p-5 text-base text-ink">
                    {t.newsPage.interested}{" "}
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="font-600 text-brand hover:text-brand-dark"
                    >
                      {COMPANY.email}
                    </a>{" "}
                    {t.newsPage.orCall}{" "}
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      className="font-600 text-brand hover:text-brand-dark"
                    >
                      {COMPANY.phone}
                    </a>
                    .
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div className="mt-12 text-center">
              <PrimaryLink to={path("contact")} data-testid="news-contact-cta">
                {t.newsPage.questions} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
              </PrimaryLink>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const JobList = ({ title, items }) => (
  <div>
    <h4 className="font-serif text-lg font-600 text-brand-dark">{title}</h4>
    <ul className="mt-3 space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-base text-muted-foreground">
          <Check className="w-5 h-5 mt-0.5 text-brand shrink-0" strokeWidth={2.5} />
          {it}
        </li>
      ))}
    </ul>
  </div>
);
